/**
 * Catálogo: servicios y profesionales (Fase 4a · US3).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useRef, useState, type PointerEvent as PE } from 'react'
import { useParams } from 'react-router'

import { useSoloLectura } from '../../auth'
import { api, ErrorApi } from '../../lib/api'
import {
    altoDesdeDelta,
    anchoDesdeX,
    leerPaneles,
    recordarPaneles,
    type PanelesCatalogo,
} from '../../lib/catalogo-paneles'
import { colorConAlfa, colorDeProfesional } from '../../lib/horarios-colores'
import type { Plan } from '../../lib/horarios-tramos'
import { HorariosNegocio } from './horarios'
import { PaletaColor } from './paleta-color'

interface Servicio {
    id: number
    nombre: string
    duracionMin: number | null
    precio?: number | null
    descripcion?: string
    enFicha?: boolean
}

interface BorradorServicio {
    nombre: string
    duracionMin: string
    precio: string
    descripcion: string
}

const BORRADOR_VACIO: BorradorServicio = { nombre: '', duracionMin: '30', precio: '', descripcion: '' }

function borradorDe(s: Servicio): BorradorServicio {
    return {
        nombre: s.nombre,
        duracionMin: String(s.duracionMin ?? 30),
        precio: typeof s.precio === 'number' ? String(s.precio) : '',
        descripcion: s.descripcion ?? '',
    }
}

function cuerpoServicio(b: BorradorServicio): {
    nombre: string
    duracionMin: number
    precio: number
    descripcion: string
} {
    return {
        nombre: b.nombre,
        duracionMin: Number(b.duracionMin),
        precio: b.precio.trim() === '' ? 0 : Number(b.precio),
        descripcion: b.descripcion.trim(),
    }
}

interface Profesional {
    id: number
    nombre: string
    servicioIds: number[]
}

export function CatalogoNegocio() {
    const { accountId } = useParams<{ accountId: string }>()
    const qc = useQueryClient()
    const soloLectura = useSoloLectura()
    const [msg, setMsg] = useState<string | null>(null)

    const servicios = useQuery({
        queryKey: ['catalogo-servicios', accountId],
        queryFn: () => api.get<{ servicios: Servicio[] }>(`/business/${accountId}/agenda/servicios`),
        enabled: Boolean(accountId),
    })
    const profesionales = useQuery({
        queryKey: ['catalogo-profesionales', accountId],
        queryFn: () =>
            api.get<{ profesionales: Profesional[] }>(`/business/${accountId}/agenda/profesionales`),
        enabled: Boolean(accountId),
    })

    const invalidar = async () => {
        await qc.invalidateQueries({ queryKey: ['catalogo-servicios', accountId] })
        await qc.invalidateQueries({ queryKey: ['catalogo-profesionales', accountId] })
        await qc.invalidateQueries({ queryKey: ['calendario', accountId] })
        await qc.invalidateQueries({ queryKey: ['horarios', accountId] })
    }

    const [altaServAbierta, setAltaServAbierta] = useState(false)
    const [editandoServicio, setEditandoServicio] = useState<number | null>(null)
    const [borrador, setBorrador] = useState<BorradorServicio>(BORRADOR_VACIO)
    const guardarServ = useMutation({
        mutationFn: () => {
            const cuerpo = cuerpoServicio(borrador)
            if (editandoServicio !== null) {
                return api.patch<Servicio>(`/business/${accountId}/agenda/servicios/${editandoServicio}`, cuerpo)
            }
            return api.post<Servicio>(`/business/${accountId}/agenda/servicios`, cuerpo)
        },
        onSuccess: async (servicio) => {
            const eraEdicion = editandoServicio !== null
            setBorrador(BORRADOR_VACIO)
            setAltaServAbierta(false)
            setEditandoServicio(null)
            if (eraEdicion && servicio.enFicha === false) {
                setMsg('Guardado en la agenda. El asistente no tiene este servicio en su ficha.')
            } else {
                setMsg(eraEdicion ? 'Servicio guardado' : 'Servicio creado')
            }
            await invalidar()
        },
        onError: (e) => setMsg(e instanceof ErrorApi ? e.message : 'Error'),
    })

    const bajaServ = useMutation({
        mutationFn: (id: number) => api.delete(`/business/${accountId}/agenda/servicios/${id}`),
        onSuccess: async () => {
            setMsg('Servicio eliminado')
            await invalidar()
        },
        onError: (e) => {
            if (e instanceof ErrorApi && e.message === 'prohibido_catalogo') {
                setMsg('No se puede borrar: hay citas futuras con este servicio')
                return
            }
            setMsg(e instanceof ErrorApi ? e.message : 'Error al borrar')
        },
    })

    const colores = useQuery({
        queryKey: ['horarios', accountId],
        queryFn: () =>
            api.get<{ profesionales: { id: number; color: string; plan: Plan }[] }>(
                `/business/${accountId}/agenda/horarios`,
            ),
        enabled: Boolean(accountId),
    })

    const [editando, setEditando] = useState(false)
    const [eligiendoColor, setEligiendoColor] = useState<number | null>(null)
    const [paneles, setPaneles] = useState<PanelesCatalogo>(leerPaneles)
    const marco = useRef<HTMLDivElement>(null)
    const arrastre = useRef<{ tipo: 'ancho' | 'alto'; y0: number; alto0: number } | null>(null)
    const [altaProfAbierta, setAltaProfAbierta] = useState(false)
    const [nuevoProf, setNuevoProf] = useState({ nombre: '', apellidos: '', servicioIds: [] as number[] })
    const altaProf = useMutation({
        mutationFn: () =>
            api.post(`/business/${accountId}/agenda/profesionales`, {
                nombre: nuevoProf.nombre,
                apellidos: nuevoProf.apellidos || undefined,
                servicioIds: nuevoProf.servicioIds,
            }),
        onSuccess: async () => {
            setNuevoProf({ nombre: '', apellidos: '', servicioIds: [] })
            setAltaProfAbierta(false)
            setMsg('Profesional creado')
            await invalidar()
        },
        onError: (e) => setMsg(e instanceof ErrorApi ? e.message : 'Error'),
    })

    const vincular = useMutation({
        mutationFn: (p: { id: number; servicioIds: number[] }) =>
            api.put(`/business/${accountId}/agenda/profesionales/${p.id}/servicios`, {
                servicioIds: p.servicioIds,
            }),
        onSuccess: async () => {
            setMsg('Vínculos actualizados')
            await invalidar()
        },
        onError: (e) => setMsg(e instanceof ErrorApi ? e.message : 'Error'),
    })

    const bajaProf = useMutation({
        mutationFn: (id: number) => api.delete(`/business/${accountId}/agenda/profesionales/${id}`),
        onSuccess: async () => {
            setMsg('Profesional eliminado')
            await invalidar()
        },
        onError: (e) => {
            if (e instanceof ErrorApi && e.message === 'prohibido_catalogo') {
                setMsg('No se puede borrar: hay citas futuras con este profesional')
                return
            }
            setMsg(e instanceof ErrorApi ? e.message : 'Error al borrar')
        },
    })

    const cambiarColor = useMutation({
        mutationFn: (p: { id: number; color: string }) =>
            api.put<{ ok: true; color: string }>(
                `/business/${accountId}/profesionales/${p.id}/color`,
                { color: p.color },
            ),
        onSuccess: async (_r, vars) => {
            setMsg('Color actualizado')
            qc.setQueryData(
                ['horarios', accountId],
                (viejo: { profesionales: { id: number; color: string }[] } | undefined) => {
                    if (!viejo) return viejo
                    return {
                        ...viejo,
                        profesionales: viejo.profesionales.map((p) =>
                            p.id === vars.id ? { ...p, color: vars.color } : p,
                        ),
                    }
                },
            )
            await invalidar()
            await qc.invalidateQueries({ queryKey: ['auditoria-catalogo', accountId] })
        },
        onError: (e) => setMsg(e instanceof ErrorApi ? e.message : 'Error al guardar el color'),
    })

    const toggleServicioProf = (prof: Profesional, servicioId: number) => {
        const set = new Set(prof.servicioIds)
        if (set.has(servicioId)) set.delete(servicioId)
        else set.add(servicioId)
        vincular.mutate({ id: prof.id, servicioIds: [...set] })
    }

    function colorDe(id: number): string {
        return colores.data?.profesionales.find((p) => p.id === id)?.color ?? colorDeProfesional(id)
    }

    function fijar(next: PanelesCatalogo) {
        setPaneles(next)
        recordarPaneles(next)
    }

    function alMover(e: PE<HTMLDivElement>) {
        const a = arrastre.current
        if (!a) return
        if (a.tipo === 'ancho') {
            const caja = marco.current?.getBoundingClientRect()
            if (!caja) return
            fijar({ ...paneles, anchoServicios: anchoDesdeX(e.clientX, caja.left, caja.width) })
            return
        }
        fijar({ ...paneles, altoListas: altoDesdeDelta(a.alto0, e.clientY - a.y0) })
    }

    function soltar() {
        arrastre.current = null
    }

    return (
        <div className="space-y-4" data-testid="catalogo-negocio">
            <header className="flex flex-wrap items-center justify-between gap-2">
                <h2 className="text-base font-semibold text-neutral-100">Trabajadores y servicios</h2>
                {!soloLectura && (
                <button
                    type="button"
                    data-testid="catalogo-editar"
                    aria-pressed={editando}
                    onClick={() => setEditando((v) => !v)}
                    className={
                        'min-h-11 rounded-md border px-2.5 py-1 text-[11px] md:min-h-0 ' +
                        (editando
                            ? 'border-sky-500/50 bg-sky-500/15 text-sky-200'
                            : 'border-neutral-800 text-neutral-400 hover:text-neutral-200')
                    }
                >
                    {editando ? 'Listo' : 'Editar'}
                </button>
                )}
            </header>

            {msg && <p className="text-xs text-neutral-400" role="status">{msg}</p>}
            {editando && (
                <>
                    <p className="hidden text-[11px] text-neutral-500 md:block">
                        Arrastra el borde entre las dos ventanas, o el de abajo, para cambiar lo que ocupan.
                    </p>
                    <p className="text-[11px] text-neutral-500 md:hidden">
                        Arrastra el borde de abajo para cambiar el alto de las listas.
                    </p>
                </>
            )}

            <div
                ref={marco}
                className={
                    editando
                        ? 'relative grid select-none gap-0 md:grid-cols-[var(--ancho-servicios)_8px_minmax(0,1fr)]'
                        : 'relative grid gap-4 md:grid-cols-[var(--ancho-servicios)_minmax(0,1fr)]'
                }
                style={{ ['--ancho-servicios' as string]: `${paneles.anchoServicios}%` }}
                onPointerMove={alMover}
                onPointerUp={soltar}
                onPointerCancel={soltar}
            >
            <section className="space-y-2">
                <div className="flex items-center justify-between gap-2">
                    <h3 className="text-sm font-medium text-neutral-200">Servicios</h3>
                    {!soloLectura && (
                    <button
                        type="button"
                        aria-label="Añadir servicio"
                        aria-expanded={altaServAbierta}
                        onClick={() => {
                            setEditandoServicio(null)
                            setBorrador(BORRADOR_VACIO)
                            setAltaServAbierta((v) => !v)
                        }}
                        className="flex h-11 w-11 items-center justify-center rounded-md border border-sky-500/40 bg-sky-500/10 text-sm font-medium text-sky-200 hover:bg-sky-500/20 md:h-7 md:w-7"
                    >
                        +
                    </button>
                    )}
                </div>
                <ul className="space-y-1 overflow-y-auto pr-1" style={{ maxHeight: paneles.altoListas }}>
                    {servicios.data?.servicios.map((s) => (
                        <li
                            key={s.id}
                            className="rounded border border-neutral-800 px-3 py-2 text-xs text-neutral-300"
                        >
                            <div className="flex flex-wrap items-center gap-2">
                                <span className="font-medium text-neutral-100">{s.nombre}</span>
                                <span className="text-neutral-500">{s.duracionMin ?? '—'} min</span>
                                {typeof s.precio === 'number' && s.precio > 0 && (
                                    <span className="text-neutral-500">{s.precio} €</span>
                                )}
                                {!soloLectura && (
                                    <>
                                <button
                                    type="button"
                                    data-testid={`editar-servicio-${s.id}`}
                                    className="ml-auto text-sky-300/80 hover:text-sky-200"
                                    onClick={() => {
                                        setAltaServAbierta(false)
                                        setEditandoServicio(s.id)
                                        setBorrador(borradorDe(s))
                                    }}
                                >
                                    editar
                                </button>
                                <button
                                    type="button"
                                    className="text-rose-400/80 hover:text-rose-300"
                                    onClick={() => {
                                        if (confirm(`¿Eliminar «${s.nombre}»?`)) bajaServ.mutate(s.id)
                                    }}
                                >
                                    eliminar
                                </button>
                                    </>
                                )}
                            </div>
                            {s.descripcion && (
                                <p className="mt-1 line-clamp-3 whitespace-pre-wrap text-neutral-500">{s.descripcion}</p>
                            )}
                        </li>
                    ))}
                </ul>
                {(altaServAbierta || editandoServicio !== null) && (
                <FormularioServicio
                    borrador={borrador}
                    editando={editandoServicio !== null}
                    pendiente={guardarServ.isPending}
                    onChange={setBorrador}
                    onCerrar={() => {
                        setAltaServAbierta(false)
                        setEditandoServicio(null)
                        setBorrador(BORRADOR_VACIO)
                    }}
                    onSubmit={() => guardarServ.mutate()}
                />
                )}
            </section>

            {editando && (
                <div
                    role="separator"
                    aria-orientation="vertical"
                    aria-label="Ancho de servicios"
                    className="hidden cursor-col-resize md:block"
                    onPointerDown={(e) => {
                        marco.current?.setPointerCapture(e.pointerId)
                        arrastre.current = { tipo: 'ancho', y0: e.clientY, alto0: paneles.altoListas }
                    }}
                >
                    <span className="mx-auto mt-8 block h-16 w-1 rounded-full bg-sky-400/70" />
                </div>
            )}

            <section className="space-y-2">
                <div className="flex items-center justify-between gap-2">
                    <h3 className="text-sm font-medium text-neutral-200">Profesionales</h3>
                    {!soloLectura && (
                    <button
                        type="button"
                        aria-label="Añadir profesional"
                        aria-expanded={altaProfAbierta}
                        onClick={() => setAltaProfAbierta((v) => !v)}
                        className="flex h-11 w-11 items-center justify-center rounded-md border border-sky-500/40 bg-sky-500/10 text-sm font-medium text-sky-200 hover:bg-sky-500/20 md:h-7 md:w-7"
                    >
                        +
                    </button>
                    )}
                </div>
                <ul className="space-y-2 overflow-y-auto pr-1" style={{ maxHeight: paneles.altoListas }}>
                    {profesionales.data?.profesionales.map((p) => (
                        <li
                            key={p.id}
                            className="space-y-2 rounded border border-neutral-800/80 px-3 py-2 text-xs"
                            style={{
                                borderLeftWidth: 3,
                                borderLeftColor: colorDe(p.id),
                                background: colorConAlfa(colorDe(p.id), 0.12),
                            }}
                        >
                            <div className="flex flex-wrap items-center gap-2">
                                <button
                                    type="button"
                                    data-testid={`color-profesional-${p.id}`}
                                    aria-label={`Color de ${p.nombre}`}
                                    aria-expanded={editando || eligiendoColor === p.id}
                                    onClick={() => {
                                        if (soloLectura) return
                                        setEligiendoColor((id) => (id === p.id ? null : p.id))
                                    }}
                                    className="inline-block h-7 w-7 shrink-0 rounded-full border border-white/20 md:h-4 md:w-4"
                                    style={{ background: colorDe(p.id) }}
                                />
                                <span className="font-medium text-neutral-100">{p.nombre}</span>
                                {!soloLectura && (
                                <button
                                    type="button"
                                    className="ml-auto text-rose-400/80 hover:text-rose-300"
                                    onClick={() => {
                                        if (confirm(`¿Eliminar «${p.nombre}»?`)) bajaProf.mutate(p.id)
                                    }}
                                >
                                    eliminar
                                </button>
                                )}
                            </div>
                            {(editando || eligiendoColor === p.id) && (
                                <PaletaColor
                                    actual={colorDe(p.id)}
                                    nombre={p.nombre}
                                    disabled={cambiarColor.isPending}
                                    onElegir={(color) => cambiarColor.mutate({ id: p.id, color })}
                                />
                            )}
                            <div className="flex flex-wrap gap-1">
                                {servicios.data?.servicios.map((s) => {
                                    const on = p.servicioIds.includes(s.id)
                                    return (
                                        <button
                                            key={s.id}
                                            type="button"
                                            onClick={() => {
                                                if (soloLectura) return
                                                toggleServicioProf(p, s.id)
                                            }}
                                            className={
                                                'rounded border px-2 py-0.5 ' +
                                                (on
                                                    ? 'border-sky-500/50 bg-sky-500/15 text-sky-200'
                                                    : 'border-neutral-800 text-neutral-500')
                                            }
                                        >
                                            {s.nombre}
                                        </button>
                                    )
                                })}
                            </div>
                        </li>
                    ))}
                </ul>
                {altaProfAbierta && (
                <form
                    className="space-y-2.5 rounded-lg border border-sky-500/25 bg-gradient-to-b from-sky-500/10 to-neutral-950/40 p-2.5"
                    onSubmit={(e) => {
                        e.preventDefault()
                        altaProf.mutate()
                    }}
                >
                    <label className="block space-y-1">
                        <span className="text-[10px] font-medium uppercase tracking-wide text-sky-200/70">
                            Nombre
                        </span>
                        <input
                            data-testid="nuevo-profesional-nombre"
                            placeholder="Elena"
                            value={nuevoProf.nombre}
                            onChange={(e) => setNuevoProf({ ...nuevoProf, nombre: e.target.value })}
                            className="w-full rounded-md border border-neutral-700 bg-neutral-900 px-2.5 py-1.5 text-xs text-neutral-100 outline-none placeholder:text-neutral-600 focus:border-sky-500/50 focus:ring-2 focus:ring-sky-500/20"
                            required
                        />
                    </label>
                    <label className="block space-y-1">
                        <span className="text-[10px] font-medium uppercase tracking-wide text-sky-200/70">
                            Apellidos
                        </span>
                        <input
                            placeholder="opcional"
                            value={nuevoProf.apellidos}
                            onChange={(e) => setNuevoProf({ ...nuevoProf, apellidos: e.target.value })}
                            className="w-full rounded-md border border-neutral-700 bg-neutral-900 px-2.5 py-1.5 text-xs text-neutral-100 outline-none placeholder:text-neutral-600 focus:border-sky-500/50 focus:ring-2 focus:ring-sky-500/20"
                        />
                    </label>
                    <button
                        type="submit"
                        data-testid="crear-profesional"
                        disabled={altaProf.isPending}
                        className="min-h-11 w-full rounded-md bg-sky-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-sky-400 disabled:opacity-50 md:min-h-0"
                    >
                        Guardar
                    </button>
                </form>
                )}
            </section>
                {editando && (
                    <div
                        role="separator"
                        aria-orientation="horizontal"
                        aria-label="Alto de las listas"
                        className="col-span-full cursor-row-resize py-1"
                        onPointerDown={(e) => {
                            marco.current?.setPointerCapture(e.pointerId)
                            arrastre.current = { tipo: 'alto', y0: e.clientY, alto0: paneles.altoListas }
                        }}
                    >
                        <span className="mx-auto block h-1 w-24 rounded-full bg-sky-400/70" />
                    </div>
                )}
            </div>

            <HorariosNegocio embebido />
        </div>
    )
}

function FormularioServicio(props: {
    borrador: BorradorServicio
    editando: boolean
    pendiente: boolean
    onChange: (b: BorradorServicio) => void
    onCerrar: () => void
    onSubmit: () => void
}) {
    const { borrador, editando, pendiente, onChange, onCerrar, onSubmit } = props
    const campo =
        'w-full rounded-md border border-neutral-700 bg-neutral-900 px-2.5 py-1.5 text-xs text-neutral-100 outline-none placeholder:text-neutral-600 focus:border-sky-500/50 focus:ring-2 focus:ring-sky-500/20'
    const etiqueta = 'text-[10px] font-medium uppercase tracking-wide text-sky-200/70'
    return (
        <form
            className="space-y-2.5 rounded-lg border border-sky-500/25 bg-gradient-to-b from-sky-500/10 to-neutral-950/40 p-2.5"
            onSubmit={(e) => {
                e.preventDefault()
                onSubmit()
            }}
        >
            <p className="text-[11px] font-medium text-sky-100">{editando ? 'Editar servicio' : 'Nuevo servicio'}</p>
            <label className="block space-y-1">
                <span className={etiqueta}>Nombre</span>
                <input
                    data-testid="nuevo-servicio-nombre"
                    placeholder="Limpieza, revisión…"
                    value={borrador.nombre}
                    onChange={(e) => onChange({ ...borrador, nombre: e.target.value })}
                    className={campo}
                    required
                />
            </label>
            <label className="block space-y-1">
                <span className={etiqueta}>Duración</span>
                <span className="flex items-center overflow-hidden rounded-md border border-neutral-700 bg-neutral-900 focus-within:border-sky-500/50 focus-within:ring-2 focus-within:ring-sky-500/20">
                    <input
                        type="number"
                        min={5}
                        step={5}
                        value={borrador.duracionMin}
                        onChange={(e) => onChange({ ...borrador, duracionMin: e.target.value })}
                        className="min-w-0 flex-1 bg-transparent px-2.5 py-1.5 text-xs text-neutral-100 outline-none"
                        required
                    />
                    <span className="shrink-0 border-l border-neutral-700 px-2.5 py-1.5 text-[10px] font-medium uppercase tracking-wide text-neutral-500">
                        min
                    </span>
                </span>
            </label>
            <label className="block space-y-1">
                <span className={etiqueta}>Precio</span>
                <span className="flex items-center overflow-hidden rounded-md border border-neutral-700 bg-neutral-900 focus-within:border-sky-500/50 focus-within:ring-2 focus-within:ring-sky-500/20">
                    <input
                        type="number"
                        min={0}
                        step="0.01"
                        value={borrador.precio}
                        onChange={(e) => onChange({ ...borrador, precio: e.target.value })}
                        placeholder="0"
                        className="min-w-0 flex-1 bg-transparent px-2.5 py-1.5 text-xs text-neutral-100 outline-none"
                    />
                    <span className="shrink-0 border-l border-neutral-700 px-2.5 py-1.5 text-[10px] font-medium uppercase tracking-wide text-neutral-500">
                        €
                    </span>
                </span>
            </label>
            <label className="block space-y-1">
                <span className={etiqueta}>En qué consiste</span>
                <textarea
                    data-testid="servicio-descripcion"
                    value={borrador.descripcion}
                    maxLength={800}
                    rows={4}
                    placeholder="Qué se hace, para quién es, qué hay que traer. El asistente se lo puede decir a un cliente nuevo."
                    onChange={(e) => onChange({ ...borrador, descripcion: e.target.value })}
                    className={campo}
                />
            </label>
            <div className="flex gap-2">
                <button
                    type="submit"
                    data-testid="crear-servicio"
                    disabled={pendiente}
                    className="min-h-11 flex-1 rounded-md bg-sky-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-sky-400 disabled:opacity-50 md:min-h-0"
                >
                    Guardar
                </button>
                <button
                    type="button"
                    onClick={onCerrar}
                    className="min-h-11 rounded-md border border-neutral-700 px-3 py-1.5 text-xs text-neutral-300 hover:text-neutral-100 md:min-h-0"
                >
                    Cancelar
                </button>
            </div>
        </form>
    )
}
